/**
 * Freshness Manager
 *
 * Centralized service that tracks when each data domain was last synced
 * and triggers refreshes based on TTL and version drift.
 *
 * See docs/specs/mobile/data-freshness.mobile.md for full spec.
 *
 * Key behaviors:
 * - TTL-based: on app foreground, stale domains refresh in parallel
 * - Version-based: X-Data-Version header triggers targeted refreshes
 * - Deduplication: at most one in-flight refresh per domain
 * - Version tracking: lastRequestedVersion vs lastCompletedVersion prevents missed updates
 */

import { AppState, type AppStateStatus, type NativeEventSubscription } from 'react-native';
import { createLogger } from '../utils/logger';
import {
  type DataDomain,
  KNOWN_DOMAINS,
  FRESHNESS_POLICIES,
  FreshnessVersionStorage,
  FreshnessDomainStateStorage,
} from '../generated/sync/sync-config';

const log = createLogger('FreshnessManager');

// ─── Types ───────────────────────────────────────────────────────

interface DomainState {
  /** Timestamp of last successful refresh (persisted) */
  lastSyncedAt: number;
  /** X-Data-Version when the last refresh completed */
  lastCompletedVersion: number;
  /** Highest X-Data-Version that triggered a refresh */
  lastRequestedVersion: number;
  /** In-flight refresh promise (null if idle) */
  inflight: Promise<void> | null;
}

interface DomainPolicy {
  domain: DataDomain;
  ttlMs: number | null;
  refresh: () => Promise<void>;
}

// ─── Freshness Manager ──────────────────────────────────────────

class FreshnessManagerService {
  private domains = new Map<DataDomain, DomainState>();
  private policies = new Map<DataDomain, DomainPolicy>();
  private userDataVersion = 0;
  private appStateSubscription: NativeEventSubscription | null = null;
  private initialized = false;
  /**
   * Per-domain serialized form of the last payload we wrote to
   * AsyncStorage. Used by `persistDomainState` to skip redundant
   * writes when the freshness state hasn't actually changed.
   *
   * The duplicate-write hazard the dedup catches:
   *
   *   AppNavigator calls `fetchProfile()` on auth-change → first
   *   `GET /me` → response interceptor sees `X-Stale-Domains:
   *   userProfile` + version drift → calls
   *   `refreshDomains(['userProfile'], serverVersion)` → `executeRefresh`
   *   runs the registered callback (also `fetchProfile()`) → second
   *   `GET /me` → both fetches call `markFresh('userProfile')` AND
   *   `executeRefresh` writes again at its tail. Three back-to-back
   *   persists of byte-identical state in the same millisecond, every
   *   sign-in. Caught in CACHE-003's failure-branch call log
   *   (timestamp 1780910022692, run 2026-06-08T09-11-27-596Z).
   *
   * Dedup at the storage boundary handles ALL the redundant call
   * sites with one guard instead of refactoring each writer to know
   * whether someone else just wrote.
   */
  private lastPersistedPayload = new Map<DataDomain, string>();
  /**
   * **Soft-mute switch** for the auto-sync triggers, used by E2E stories
   * that don't exercise the freshness system.
   *
   * When `false`:
   *   - the AppState `active` event no longer fires `refreshStale()`
   *     (no foreground refresh storm pollutes the test's call log).
   *   - `refreshStale()` itself returns immediately (defensive — covers
   *     callers that invoke it bypassing the AppState handler).
   *
   * Crucially, the EXPLICIT refresh paths (`refreshDomains(domains)`,
   * `markStale(domain)`, response-interceptor-driven refresh on
   * `X-Stale-Domains`, store-driven `load()` falling through to fetch)
   * are NOT suppressed. Tests that want to assert sync behavior on
   * demand still can; only the background noise goes away. This is
   * the "soft mute" mode the user picked.
   *
   * Default `true` in production — auto-sync is the normal app
   * behavior. The E2E engine flips this off per-story via the
   * `dev.set-auto-sync` intent when `story.disableSync === true`.
   */
  private autoSyncEnabled = true;

  /**
   * Register a data domain with its refresh function.
   * TTL comes from the generated FRESHNESS_POLICIES.
   */
  register(domain: DataDomain, refresh: () => Promise<void>): void {
    const policyConfig = FRESHNESS_POLICIES.find((p) => p.domain === domain);
    this.policies.set(domain, {
      domain,
      ttlMs: policyConfig?.ttlMs ?? null,
      refresh,
    });
    this.domains.set(domain, {
      lastSyncedAt: 0,
      lastCompletedVersion: 0,
      lastRequestedVersion: 0,
      inflight: null,
    });
  }

  /**
   * Load persisted timestamps + version from AsyncStorage.
   * Call once at app startup after registering all domains, before auth.
   * Must be awaited — ensures the version interceptor has the correct baseline
   * before the first API call.
   */
  async loadState(): Promise<void> {
    if (this.initialized) return;
    await this.loadPersistedState();
    this.initialized = true; // set after load succeeds, so a failure allows retry
  }

  /**
   * Start listening for app foreground events to refresh stale domains.
   * Call after auth is initialized (domains may need auth to refresh).
   */
  startListening(): void {
    if (this.appStateSubscription) return;
    this.appStateSubscription = AppState.addEventListener('change', this.handleAppState);
  }

  /**
   * Whether a domain's data is currently fresh. Combines BOTH freshness
   * signals the manager tracks:
   *
   *   1. Version drift (primary, server-driven). When the backend has
   *      bumped `X-Data-Version` past what this domain last completed
   *      a refresh for (`state.lastCompletedVersion < userDataVersion`),
   *      the data is known stale regardless of how recent the sync was.
   *      Catches the case where a prior session learned about a version
   *      bump but didn't finish refreshing this domain before backgrounding.
   *
   *   2. TTL (backstop, time-based). When the server hasn't told us
   *      anything contradicting our cache OR we haven't talked to it yet
   *      (cold start), trust the cache for the policy's `ttlMs` window
   *      since `lastSyncedAt`. After that, treat as stale and force a
   *      refresh on next access.
   *
   * Returns true (= fresh) for domains:
   *   - not registered (caller asked about an unknown domain; defaults
   *     to fresh to avoid spurious refresh storms)
   *   - whose version is current AND (either no TTL declared OR
   *     `lastSyncedAt` is within TTL of now)
   *
   * Returns false when either signal says stale: known version drift,
   * OR TTL elapsed, OR `lastSyncedAt === 0` (never synced).
   */
  isFresh(domain: DataDomain): boolean {
    const state = this.domains.get(domain);
    if (!state) return true;
    // Version drift — primary, server-truth signal. If a prior session
    // saw a newer version but didn't finish pulling this domain, the
    // cache is stale even if the TTL is still nominally valid.
    if (state.lastCompletedVersion < this.userDataVersion) return false;
    const policy = this.policies.get(domain);
    if (!policy?.ttlMs) return true;
    if (state.lastSyncedAt === 0) return false;
    return Date.now() - state.lastSyncedAt <= policy.ttlMs;
  }

  /**
   * Timestamp of the last successful sync for a domain (epoch ms), or
   * null if the domain isn't registered or has never synced. Use
   * `isFresh(domain)` for the common "is this still good?" check;
   * reach for `lastSyncedAt` only when you need to render the timestamp
   * (e.g. "Last updated 3 minutes ago" UI).
   */
  lastSyncedAt(domain: DataDomain): number | null {
    const state = this.domains.get(domain);
    if (!state || state.lastSyncedAt === 0) return null;
    return state.lastSyncedAt;
  }

  /**
   * Check all domains and refresh any that are stale (TTL expired).
   * Called on app foreground.
   */
  async refreshStale(): Promise<void> {
    // Soft-muted by `dev.set-auto-sync`: short-circuit the foreground/
    // TTL-driven refresh path entirely. Explicit refreshDomains() calls
    // still fire — see the `autoSyncEnabled` docstring.
    if (!this.autoSyncEnabled) return;
    const now = Date.now();
    const stale: DataDomain[] = [];

    for (const [domain, state] of this.domains) {
      const policy = this.policies.get(domain);
      if (!policy?.ttlMs) continue; // no TTL (e.g. lessons — version drift only)
      if (now - state.lastSyncedAt > policy.ttlMs) {
        stale.push(domain);
      }
    }

    if (stale.length === 0) return;

    log.debug('Refreshing stale domains', { domains: stale });
    await Promise.allSettled(stale.map((d) => this.refreshDomain(d)));
  }

  /**
   * Force-refresh specific domains. Triggered by version drift or explicit action.
   * Updates lastRequestedVersion per domain for re-refresh detection.
   */
  async refreshDomains(domains: DataDomain[], triggerVersion?: number): Promise<void> {
    for (const domain of domains) {
      if (!KNOWN_DOMAINS.has(domain)) continue;
      if (triggerVersion) {
        const state = this.domains.get(domain);
        if (state) {
          state.lastRequestedVersion = Math.max(state.lastRequestedVersion, triggerVersion);
        }
      }
    }

    await Promise.allSettled(domains.map((d) => this.refreshDomain(d)));
  }

  /**
   * Mark a domain's version as current (matching userDataVersion).
   * Called after a sync queue push succeeds — prevents the response interceptor
   * from triggering a redundant refresh for our own mutation.
   */
  markVersionCurrent(domain: DataDomain): void {
    const state = this.domains.get(domain);
    if (!state) return;
    state.lastCompletedVersion = this.userDataVersion;
    state.lastRequestedVersion = this.userDataVersion;
    state.lastSyncedAt = Date.now(); // reset TTL — push response has fresh data
    this.persistDomainState(domain, state);
  }

  /**
   * Force-refresh all registered domains. Used by dev tools.
   */
  async refreshAll(): Promise<void> {
    const allDomains = Array.from(this.policies.keys());
    log.info('Refreshing all domains', { domains: allDomains });
    await Promise.allSettled(allDomains.map((d) => this.refreshDomain(d)));
  }

  /**
   * Mark a domain as freshly synced. Called by stores after a self-driven
   * fetch completes (e.g. `courseStore.load()` after `api.getCourses()`).
   *
   * Updates BOTH freshness signals — must mirror `executeRefresh` (lines
   * 368-370) or self-driven fetches leave `lastCompletedVersion` permanently
   * behind `userDataVersion`. That causes `isFresh()` to return `false`
   * after every store-driven sync, so the next `load()` falls through to
   * the API again — every consult triggers a redundant GET. This was the
   * root cause of CACHE-002's first-launch double-GET (two redundant
   * GET /api/courses calls in the post-restart boot, one from
   * AppNavigator's useEffect and one from the test's dev.refresh-courses).
   */
  markFresh(domain: DataDomain): void {
    const state = this.domains.get(domain);
    if (!state) return;
    state.lastSyncedAt = Date.now();
    state.lastCompletedVersion = state.lastRequestedVersion || this.userDataVersion;
    this.persistDomainState(domain, state);
  }

  /**
   * Mark a domain as stale — forces `isFresh(domain)` to return false on
   * the next consultation. Sets `lastSyncedAt = 0`, persists immediately.
   *
   * Intended for affordances like "pull to refresh" that want the next
   * render to refetch without waiting for TTL elapse. CACHE-002's TTL
   * branch uses the same shape but reaches it via the framework's
   * `WriteStorage(@fluencia/freshness/domain/courses, {lastSyncedAt:0,…})`
   * primitive directly — same persisted state, no need for a runtime
   * method call. No-op for unknown domains.
   */
  markStale(domain: DataDomain): void {
    const state = this.domains.get(domain);
    if (!state) return;
    state.lastSyncedAt = 0;
    this.persistDomainState(domain, state);
  }

  /**
   * Get the current user data version (for request header).
   */
  getUserDataVersion(): number {
    return this.userDataVersion;
  }

  /**
   * Dev-only test escape hatch — reset the in-memory + persisted
   * freshness state to a clean baseline so the NEXT response carrying
   * `X-Data-Version > 0` triggers a refresh, while NOT marking any
   * domain TTL-stale (which would cascade into auto-refreshes that
   * consume the test's `for-next` injection before its actual
   * `Do(...)` step fires).
   *
   * Two values matter:
   *
   *   `userDataVersion`  → reset to 0. The strict-less-than check in
   *     `apiClient.ts:200` (serverVersion > localVersion) now succeeds
   *     for any positive injected serverVersion, regardless of what
   *     version the device had previously bumped to.
   *
   *   `lastSyncedAt`     → set to NOW (not 0). With 0, `refreshStale`
   *     would consider every domain stale (now − 0 > ttlMs always
   *     true) and AppState's 'active' event would fire a cascade of
   *     /me + /courses + /messages refreshes. Setting to NOW keeps
   *     TTL happy AND preserves the version-drift trigger as the
   *     only refresh path during the test.
   *
   * Without this method, E2E tests that inject `X-Data-Version: 9999`
   * work the first time but never trigger refreshes again — the
   * persisted 9999 survives across runs.
   *
   * Wired into the dev-only deep link `exp+fluencia://dev/reset-freshness`
   * (see `mobile/src/dev/devReset.ts`). Production builds strip the
   * deep-link handler via __DEV__ guards.
   */
  async resetForTesting(): Promise<void> {
    this.userDataVersion = 0;
    // Drop the dedup table BEFORE writing the reset state — otherwise
    // a prior identical reset (same lastSyncedAt millisecond on a fast
    // E2E loop) would no-op and the test would proceed on stale
    // persisted state. Tests want the reset to actually hit disk.
    this.lastPersistedPayload.clear();
    const now = Date.now();
    for (const [domain, state] of this.domains) {
      // lastSyncedAt = now → not TTL-stale → no cascade on AppState active
      state.lastSyncedAt = now;
      state.lastCompletedVersion = 0;
      state.lastRequestedVersion = 0;
      state.inflight = null;
      this.persistDomainState(domain, state);
    }
    await FreshnessVersionStorage.save(0).catch(() => {});
  }

  /**
   * Toggle the auto-sync soft-mute. See the `autoSyncEnabled` field
   * docstring for what's suppressed vs. what still fires.
   *
   * E2E only — invoked by the `dev.set-auto-sync` dev intent. Should
   * never be called from production code paths.
   */
  setAutoSyncEnabled(enabled: boolean): void {
    this.autoSyncEnabled = enabled;
  }

  /** Whether auto-sync is currently enabled. Test-side observability. */
  isAutoSyncEnabled(): boolean {
    return this.autoSyncEnabled;
  }

  /**
   * Set the user data version (from response header).
   */
  setUserDataVersion(version: number): void {
    if (version > this.userDataVersion) {
      this.userDataVersion = version;
      FreshnessVersionStorage.save(version).catch(() => {});
    }
  }

  /**
   * Parse X-Stale-Domains header, filtering unknown domains.
   */
  parseStaleDomains(header: string): DataDomain[] {
    return header
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is DataDomain => KNOWN_DOMAINS.has(s as DataDomain));
  }

  /**
   * Tear down listeners (for testing or logout).
   */
  destroy(): void {
    this.appStateSubscription?.remove();
    this.appStateSubscription = null;
    this.domains.clear();
    this.policies.clear();
    // Clear the persist-dedup table along with the rest of the state.
    // Tests reuse the singleton between cases (jest's beforeEach calls
    // destroy + creates a fresh registration), so a stale dedup entry
    // would no-op the first persist of the new test even though logically
    // it's a different lifecycle. Production logout doesn't strictly
    // need this — the persisted state on disk is also gone after a
    // sign-out path — but clearing keeps the invariant simple.
    this.lastPersistedPayload.clear();
    this.userDataVersion = 0;
    this.initialized = false;
  }

  // ─── Internal ──────────────────────────────────────────────────

  /**
   * Refresh a single domain with deduplication and version tracking.
   */
  private async refreshDomain(domain: DataDomain): Promise<void> {
    const policy = this.policies.get(domain);
    const state = this.domains.get(domain);
    if (!policy || !state) return;

    // Deduplication: return existing in-flight promise
    if (state.inflight) {
      return state.inflight;
    }

    let succeeded = false;
    state.inflight = this.executeRefresh(domain, policy, state).then(
      () => { succeeded = true; },
      (err) => { log.warn(`Refresh failed for domain ${domain}`, err); },
    );

    try {
      await state.inflight;
    } finally {
      state.inflight = null;

      // Re-refresh if a newer version arrived during the in-flight refresh.
      // Only re-refresh on success — on failure, wait for the next natural trigger
      // (foreground, version drift) to avoid infinite retry loops.
      if (succeeded && state.lastRequestedVersion > state.lastCompletedVersion) {
        log.debug('Re-refreshing domain (version drift during in-flight)', {
          domain,
          completed: state.lastCompletedVersion,
          requested: state.lastRequestedVersion,
        });
        await this.refreshDomain(domain);
      }
    }
  }

  private async executeRefresh(
    domain: DataDomain,
    policy: DomainPolicy,
    state: DomainState,
  ): Promise<void> {
    await policy.refresh();
    state.lastSyncedAt = Date.now();
    state.lastCompletedVersion = state.lastRequestedVersion || this.userDataVersion;
    this.persistDomainState(domain, state);
  }

  private handleAppState = (nextState: AppStateStatus): void => {
    if (nextState === 'active') {
      this.refreshStale().catch((err) => {
        log.warn('Failed to refresh stale domains on foreground', err);
      });
    }
  };

  private async loadPersistedState(): Promise<void> {
    try {
      // Load global user data version
      this.userDataVersion = await FreshnessVersionStorage.load();

      // Load per-domain state (timestamp + versions)
      for (const domain of this.domains.keys()) {
        const persisted = await FreshnessDomainStateStorage.load(domain);
        if (persisted) {
          const state = this.domains.get(domain)!;
          state.lastSyncedAt = persisted.lastSyncedAt || 0;
          state.lastCompletedVersion = persisted.lastCompletedVersion || 0;
          state.lastRequestedVersion = persisted.lastRequestedVersion || 0;
          // Prime the dedup table so the first write attempt for an
          // unchanged-from-disk state is correctly skipped. Without
          // this, the persist-dedup misses the first redundant write
          // after a cold boot.
          this.lastPersistedPayload.set(
            domain,
            JSON.stringify({
              lastSyncedAt: state.lastSyncedAt,
              lastCompletedVersion: state.lastCompletedVersion,
              lastRequestedVersion: state.lastRequestedVersion,
            }),
          );
        }
      }
    } catch (err) {
      log.warn('Failed to load freshness state', err);
    }
  }

  private persistDomainState(domain: DataDomain, state: DomainState): void {
    const payload = {
      lastSyncedAt: state.lastSyncedAt,
      lastCompletedVersion: state.lastCompletedVersion,
      lastRequestedVersion: state.lastRequestedVersion,
    };
    // Dedup against the last successfully-queued payload. Skips the
    // redundant 2nd + 3rd writes when `markFresh` (inside fetchProfile)
    // and `executeRefresh` (the outer freshness flow) both fire for
    // the same domain in the same tick. See `lastPersistedPayload`
    // docstring for the exact cascade.
    const serialized = JSON.stringify(payload);
    if (this.lastPersistedPayload.get(domain) === serialized) return;
    this.lastPersistedPayload.set(domain, serialized);
    FreshnessDomainStateStorage.save(domain, payload).catch(() => {
      // Revert the cache marker so a future call retries the write.
      // Without this rollback a transient AsyncStorage failure would
      // silently desync the in-memory dedup table from disk.
      this.lastPersistedPayload.delete(domain);
    });
  }
}

// ─── Singleton ───────────────────────────────────────────────────

export const freshnessManager = new FreshnessManagerService();
